/**
 * Create / edit dialog for a DNS record (RRSet). The value editor is type-aware:
 * MX rows carry a preference, CAA rows carry flags + tag, SRV rows carry
 * priority/weight/port/target (plus the record's service/protocol selectors),
 * and simple records (A/AAAA/CNAME/NS/TXT…) are a plain value. Weighted routing
 * (routing_policy=1) adds a per-value weight on simple records.
 *
 * Note: the read endpoint returns a structured `details[]` (full per-type
 * fidelity), so on edit the structured fields (preference/flags/tag/priority/
 * port/target) are pre-filled from it; when `details` is absent we fall back to
 * the flattened `values` and those fields must be re-entered.
 */
import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  IconButton,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDnsRecordTypes,
  useCreateWafDnsRecord,
  useUpdateWafDnsRecord,
  wafExtractError,
  type WafDnsRecord,
  type WafDnsRecordInput,
  type WafDnsRecordValue,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './DnsRecordFormDialog.module.css';

/** Record types that can be edge-proxied (the Multi-CDN toggle only shows for these). */
const PROXIABLE_TYPES = new Set(['A', 'CNAME']);
/** CAA `tag` options. */
const CAA_TAGS = ['issue', 'issuewild', 'iodef'];
/** SRV transport protocol options (vendor expects the leading underscore). */
const SRV_PROTOCOLS = [
  { value: '_tcp', label: 'TCP' },
  { value: '_udp', label: 'UDP' },
  { value: '_tls', label: 'TLS' },
];
const DEFAULT_TTL = 3600;
/** Allowed TTL range (seconds). */
const TTL_MIN = 30;
const TTL_MAX = 86400;

/** A stacked label + control form cell. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      {children}
      {hint ? (
        <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{hint}</span>
      ) : null}
    </div>
  );
}

/** All possible per-value sub-fields, kept as strings for the inputs. */
interface ValueRow {
  value: string;
  weight: string;
  preference: string;
  flags: string;
  tag: string;
  priority: string;
  port: string;
  target: string;
}

const EMPTY_ROW: ValueRow = {
  value: '',
  weight: '',
  preference: '',
  flags: '',
  tag: 'issue',
  priority: '',
  port: '',
  target: '',
};

/** Sensible SRV defaults offered on create (still editable). */
const SRV_DEFAULTS = { priority: '10', weight: '10', port: '6688' };
/** Fill blank SRV numeric fields with the defaults, leaving anything already set. */
const applySrvDefaults = (r: ValueRow): ValueRow => ({
  ...r,
  priority: r.priority || SRV_DEFAULTS.priority,
  weight: r.weight || SRV_DEFAULTS.weight,
  port: r.port || SRV_DEFAULTS.port,
});

/** Default MX preference offered on create (still editable). */
const MX_DEFAULT_PREFERENCE = '10';
const applyMxDefaults = (r: ValueRow): ValueRow => ({
  ...r,
  preference: r.preference || MX_DEFAULT_PREFERENCE,
});

/** Drop the trailing `.zone` (or the bare zone) so the input shows only the relative name. */
function stripZone(full: string, zone?: string): string {
  if (!zone || !full) return full;
  if (full === zone) return '';
  const suffix = `.${zone}`;
  return full.endsWith(suffix) ? full.slice(0, -suffix.length) : full;
}

const numStr = (n?: number) => (n === undefined || n === null ? '' : String(n));

/** Build an editable row from a structured value (preserves per-type fields). */
function rowFromDetail(v: WafDnsRecordValue): ValueRow {
  return {
    value: v.value ?? '',
    weight: numStr(v.weight),
    preference: numStr(v.preference),
    flags: numStr(v.flags),
    tag: v.tag || 'issue',
    priority: numStr(v.priority),
    port: numStr(v.port),
    target: v.target ?? '',
  };
}

function rowsFromRecord(record?: WafDnsRecord): ValueRow[] {
  // Prefer the structured `details` (full fidelity); fall back to flat `values`.
  if (record?.details?.length) return record.details.map(rowFromDetail);
  const vals = record?.values ?? [];
  if (vals.length === 0) return [{ ...EMPTY_ROW }];
  return vals.map((v) => ({ ...EMPTY_ROW, value: v }));
}

/** Parse a string to an integer, or undefined when blank/invalid. */
function intOrUndef(s: string): number | undefined {
  const t = s.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}

export function DnsRecordFormDialog({
  serviceId,
  zoneId,
  zoneName,
  record,
  open,
  onOpenChange,
}: {
  serviceId: string;
  zoneId: string;
  /** Zone apex domain — appended to the entered name on submit, shown as a suffix. */
  zoneName?: string;
  record?: WafDnsRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const isEdit = !!record;
  const { data: recordTypes } = useWafDnsRecordTypes(serviceId);

  const [type, setType] = useState(record?.type ?? 'A');
  const [name, setName] = useState(() => stripZone(record?.name ?? '', zoneName));
  const [ttl, setTtl] = useState(String(record?.ttl ?? DEFAULT_TTL));
  const [proxied, setProxied] = useState(record?.proxied ?? false);
  const [service, setService] = useState(record?.service ?? '');
  const [protocol, setProtocol] = useState(record?.protocol || '_tcp');
  const [rows, setRows] = useState<ValueRow[]>(() => rowsFromRecord(record));

  // Reset the form whenever the dialog (re)opens for a given record.
  useEffect(() => {
    if (open) {
      setType(record?.type ?? 'A');
      setName(stripZone(record?.name ?? '', zoneName));
      setTtl(String(record?.ttl ?? DEFAULT_TTL));
      setProxied(record?.proxied ?? false);
      setService(record?.service ?? '');
      setProtocol(record?.protocol || '_tcp');
      setRows(rowsFromRecord(record));
    }
  }, [open, record, zoneName]);

  const isMX = type === 'MX';
  const isCAA = type === 'CAA';
  const isSRV = type === 'SRV';
  const structured = isMX || isCAA || isSRV;
  // CNAME is a single-target RRSet — no multi-value.
  const isSingleValue = type === 'CNAME';

  // Switching to a single-value type collapses any extra value rows;
  // switching to SRV (on create) seeds the default priority/weight/port.
  const changeType = (t: string) => {
    setType(t);
    if (t === 'CNAME') setRows((prev) => prev.slice(0, 1));
    else if (t === 'SRV' && !isEdit) setRows((prev) => prev.map(applySrvDefaults));
    else if (t === 'MX' && !isEdit) setRows((prev) => prev.map(applyMxDefaults));
  };

  const showProxied = PROXIABLE_TYPES.has(type);

  const create = useCreateWafDnsRecord(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'DNS record created' }); trackFeatureAction('waf', 'dns_record', 'create'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateWafDnsRecord(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'DNS record updated' }); trackFeatureAction('waf', 'dns_record', 'edit'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const pending = create.isPending || update.isPending;
  const ttlNum = Number(ttl);
  const ttlValid = Number.isFinite(ttlNum) && ttlNum >= TTL_MIN && ttlNum <= TTL_MAX;
  const ttlError = ttl.trim() !== '' && !ttlValid ? 'Range must fall between 30 and 86400.' : undefined;

  // A row counts as "filled" by its primary field (SRV keys off target).
  const isFilled = (r: ValueRow) => (isSRV ? r.target.trim() !== '' : r.value.trim() !== '');
  const filledRows = rows.filter(isFilled);

  // Identity of a value for duplicate detection (per record type).
  const rowKey = (r: ValueRow): string => {
    if (isMX) return `${r.value.trim()}|${r.preference.trim()}`;
    if (isCAA) return `${r.tag}|${r.value.trim()}`;
    if (isSRV) return `${r.priority.trim()}|${r.weight.trim()}|${r.port.trim()}|${r.target.trim()}`;
    return r.value.trim();
  };
  // Row indexes that repeat an earlier (filled) value — flagged as duplicates.
  const dupIdx = (() => {
    const seen = new Set<string>();
    const dups = new Set<number>();
    rows.forEach((r, i) => {
      if (!isFilled(r)) return;
      const k = rowKey(r);
      if (seen.has(k)) dups.add(i);
      else seen.add(k);
    });
    return dups;
  })();

  const rowValid = (r: ValueRow): boolean => {
    if (isMX) return intOrUndef(r.preference) !== undefined;
    if (isCAA) return r.tag.trim() !== '';
    if (isSRV)
      return (
        intOrUndef(r.priority) !== undefined &&
        intOrUndef(r.weight) !== undefined &&
        intOrUndef(r.port) !== undefined
      );
    return true;
  };

  const trimmedName = name.trim();
  // "@" (or empty) means the zone apex (root) — submit the bare zone, no suffix.
  const isRoot = trimmedName === '@' || trimmedName === '';
  const fullName = zoneName ? (isRoot ? zoneName : `${trimmedName}.${zoneName}`) : trimmedName;
  const nameOk = !!trimmedName || (isEdit && !!zoneName);

  const valid =
    nameOk &&
    ttlValid &&
    filledRows.length > 0 &&
    filledRows.every(rowValid) &&
    dupIdx.size === 0 &&
    (!isSRV || (!!service.trim() && !!protocol.trim()));

  const setRow = (i: number, patch: Partial<ValueRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((prev) => {
      let row: ValueRow = { ...EMPTY_ROW };
      if (!isEdit && isSRV) row = applySrvDefaults(row);
      else if (!isEdit && isMX) row = applyMxDefaults(row);
      return [...prev, row];
    });
  const removeRow = (i: number) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const buildValue = (r: ValueRow): WafDnsRecordValue => {
    if (isMX) return { value: r.value.trim(), preference: intOrUndef(r.preference) ?? 0 };
    if (isCAA) return { flags: 0, tag: r.tag, value: r.value.trim() };
    if (isSRV)
      return {
        priority: intOrUndef(r.priority) ?? 0,
        weight: intOrUndef(r.weight) ?? 0,
        port: intOrUndef(r.port) ?? 0,
        target: r.target.trim(),
      };
    return { value: r.value.trim() };
  };

  const submit = () => {
    if (!valid) return;
    const input: WafDnsRecordInput = {
      type,
      name: fullName,
      ttl: ttlNum,
      proxied: showProxied ? proxied : false,
      routing_policy: 0,
      values: filledRows.map(buildValue),
      ...(isSRV ? { service: service.trim(), protocol: protocol.trim() } : {}),
    };
    if (isEdit && record) update.mutate({ zoneId, id: record.id, input });
    else create.mutate({ zoneId, input });
  };

  const valuePlaceholder =
    type === 'CNAME' || type === 'NS'
      ? 'target.example.com'
      : type === 'AAAA'
        ? '2001:db8::1'
        : type === 'TXT'
          ? 'v=spf1 include:_spf.example.com ~all'
          : isMX
            ? 'mail.example.com'
            : isCAA
              ? 'letsencrypt.org'
              : '203.0.113.10';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit DNS record — ${record?.name}` : 'New DNS record'}</DialogTitle>
          <DialogDescription>
            Add a record to this zone. Multi-value records can hold several values.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Type">
              <Select value={type} onValueChange={changeType} disabled={pending || isEdit}>
                <SelectTrigger aria-label="Type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(recordTypes ?? []).map((t) => (
                    <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="TTL (seconds)">
              <Input
                type="number"
                min={TTL_MIN}
                max={TTL_MAX}
                className={styles.noSpin}
                placeholder="3600"
                value={ttl}
                onChange={(e) => setTtl(e.target.value)}
                disabled={pending}
                aria-invalid={!!ttlError}
              />
              {ttlError && (
                <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
                  {ttlError}
                </span>
              )}
            </Field>
          </div>

          <Field
            label="Name"
            hint={zoneName ? `The zone "${zoneName}" is appended automatically.` : 'The record name (FQDN), e.g. www.example.com.'}
          >
            <Input
              placeholder={zoneName ? '@ for root' : 'www.example.com'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={pending || isEdit}
              endSlot={
                zoneName && trimmedName !== '@' ? (
                  <span
                    style={{
                      color: 'var(--vn-fg-muted)',
                      fontSize: 'var(--vn-font-size-sm)',
                      whiteSpace: 'nowrap',
                      paddingRight: 'var(--vn-space-2)',
                    }}
                  >
                    .{zoneName}
                  </span>
                ) : undefined
              }
            />
          </Field>

          {isSRV && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Service" hint="e.g. _sip">
                <Input placeholder="_sip" value={service} onChange={(e) => setService(e.target.value)} disabled={pending} />
              </Field>
              <Field label="Protocol">
                <Select value={protocol} onValueChange={setProtocol} disabled={pending}>
                  <SelectTrigger aria-label="Protocol"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SRV_PROTOCOLS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {isEdit && structured && !record?.details?.length && (
            <Alert tone="info">
              {type} fields (priority/preference/flags/tag/port/target) aren’t echoed by the list, so
              re-enter them to save changes.
            </Alert>
          )}

          <Field label="Values">
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                  {isMX && (
                    <Input
                      type="number"
                      min={0}
                      className={styles.noSpin}
                      style={{ flex: '0 0 92px', width: 92 }}
                      placeholder="Pref."
                      aria-label="Preference"
                      value={r.preference}
                      onChange={(e) => setRow(i, { preference: e.target.value })}
                      disabled={pending}
                    />
                  )}
                  {isCAA && (
                    <>
                      <Input
                        className={styles.noSpin}
                        style={{ flex: '0 0 72px', width: 72 }}
                        value="0"
                        readOnly
                        disabled
                        aria-label="Flags (fixed)"
                        title="CAA flags are always 0"
                      />
                      <Select value={r.tag} onValueChange={(v) => setRow(i, { tag: v })} disabled={pending}>
                        <SelectTrigger aria-label="Tag" style={{ flex: '0 0 120px', width: 120 }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CAA_TAGS.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {isSRV && (
                    <>
                      <Input
                        type="number"
                        min={0}
                        className={styles.noSpin}
                        style={{ flex: '0 0 72px', width: 72 }}
                        placeholder="Prio."
                        aria-label="Priority"
                        value={r.priority}
                        onChange={(e) => setRow(i, { priority: e.target.value })}
                        disabled={pending}
                      />
                      <Input
                        type="number"
                        min={0}
                        className={styles.noSpin}
                        style={{ flex: '0 0 72px', width: 72 }}
                        placeholder="Weight"
                        aria-label="Weight"
                        value={r.weight}
                        onChange={(e) => setRow(i, { weight: e.target.value })}
                        disabled={pending}
                      />
                      <Input
                        type="number"
                        min={0}
                        className={styles.noSpin}
                        style={{ flex: '0 0 72px', width: 72 }}
                        placeholder="Port"
                        aria-label="Port"
                        value={r.port}
                        onChange={(e) => setRow(i, { port: e.target.value })}
                        disabled={pending}
                      />
                    </>
                  )}

                  {isSRV ? (
                    <Input
                      style={{ flex: '1 1 auto', minWidth: 0 }}
                      placeholder="target.example.com"
                      aria-label="Target"
                      value={r.target}
                      onChange={(e) => setRow(i, { target: e.target.value })}
                      disabled={pending}
                    />
                  ) : (
                    <Input
                      style={{ flex: '1 1 auto', minWidth: 0 }}
                      placeholder={valuePlaceholder}
                      aria-label="Value"
                      value={r.value}
                      onChange={(e) => setRow(i, { value: e.target.value })}
                      disabled={pending}
                    />
                  )}

                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Remove value"
                    onClick={() => removeRow(i)}
                    disabled={pending || rows.length <= 1}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                  </div>
                  {dupIdx.has(i) && (
                    <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
                      Duplicate value
                    </span>
                  )}
                </div>
              ))}
              {!isSingleValue && (
                <div>
                  <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addRow} disabled={pending}>
                    Add value
                  </Button>
                </div>
              )}
            </div>
          </Field>

          {showProxied && (
            <Field label="Multi-CDN" hint="Route this record's traffic through the CDN/WAF edge.">
              <div style={{ display: 'inline-flex' }}>
                <Switch checked={proxied} onCheckedChange={setProxied} disabled={pending} />
              </div>
            </Field>
          )}
        </DialogBody>
        <DialogFooter>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !valid}>
            {isEdit ? 'Save changes' : 'Create record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DnsRecordFormDialog;
